import { highlightSearchMatch } from '@components'

import { Text } from '@billy/design-system'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { EXPERT_TRIAL_BALANCE_COLUMN_WIDTHS } from '../constants/expertTrialBalanceColumnWidths'
import { TrialBalanceAmount } from '../elements/ExpertTrialBalanceTable/elements/TrialBalanceAmount'
import type { ExpertTrialBalanceRow } from '../types/expertTrialBalanceView'

export type ExpertTrialBalanceColumnId = 'accountNumber' | 'balance' | 'balancePrevious' | 'credit' | 'debit' | 'label'

export type ExpertTrialBalanceColumnAlign = 'end' | 'start'

export interface ExpertTrialBalanceColumnDef {
  align?: ExpertTrialBalanceColumnAlign
  Cell: (row: ExpertTrialBalanceRow) => ReactNode
  header: ReactNode
  highlighted?: boolean
  id: ExpertTrialBalanceColumnId
  width?: string | number
}

export const useExpertTrialBalanceColumns = (searchTerm = ''): ExpertTrialBalanceColumnDef[] => {
  const { t } = useTranslation()

  return useMemo(
    (): ExpertTrialBalanceColumnDef[] => [
      {
        Cell: (row) => <>{highlightSearchMatch(row.accountNumber, searchTerm)}</>,
        header: t('expert_trial_balance.table.column.account_number'),
        id: 'accountNumber',
        width: EXPERT_TRIAL_BALANCE_COLUMN_WIDTHS.accountNumber,
      },
      {
        Cell: (row) => (
          <Text as="span" inherit title={row.label}>
            {highlightSearchMatch(row.label, searchTerm)}
          </Text>
        ),
        header: t('expert_trial_balance.table.column.label'),
        id: 'label',
      },
      {
        align: 'end',
        Cell: (row) => <TrialBalanceAmount value={row.values[0]?.debit} />,
        header: t('expert_trial_balance.table.column.debit'),
        id: 'debit',
        width: EXPERT_TRIAL_BALANCE_COLUMN_WIDTHS.amount,
      },
      {
        align: 'end',
        Cell: (row) => <TrialBalanceAmount value={row.values[0]?.credit} />,
        header: t('expert_trial_balance.table.column.credit'),
        id: 'credit',
        width: EXPERT_TRIAL_BALANCE_COLUMN_WIDTHS.amount,
      },
      {
        align: 'end',
        Cell: (row) => <TrialBalanceAmount value={row.values[0]?.closingBalance} />,
        header: t('expert_trial_balance.table.column.balance'),
        highlighted: true,
        id: 'balance',
        width: EXPERT_TRIAL_BALANCE_COLUMN_WIDTHS.amount,
      },
      {
        align: 'end',
        Cell: (row) => <TrialBalanceAmount value={row.values[1]?.closingBalance} />,
        header: t('expert_trial_balance.table.column.balance_previous'),
        id: 'balancePrevious',
        width: EXPERT_TRIAL_BALANCE_COLUMN_WIDTHS.amount,
      },
    ],
    [searchTerm, t],
  )
}
